import { useModalStore } from '@stores/modalStore';
import { Sidebar, useSidebarStore } from '@stores/sidebarStore';
import type { SidebarType } from '@/registries/sidebars';
import { watch, onUnmounted } from 'vue';

/**
 * Intercepts the browser back button to close modals, sidebar tabs, and sidebars
 * instead of navigating away. Each "layer" (modal open, sidebar open, tab switch)
 * pushes a history entry; pressing back pops it and closes the corresponding layer.
 *
 * Must be called once in the root component (App.vue) after stores are available.
 *
 * We keep an ordered `layers` stack that mirrors, 1:1, the history entries we
 * pushed. Because the browser history is LIFO, the last element of `layers` always
 * corresponds to the entry a single back() would pop — so back navigation just acts
 * on `layers[last]`. Crucially, each layer records *which* sidebar it belongs to.
 * That keeps tab history isolated per sidebar: when sidebars stack and the top one
 * closes, we only unwind that sidebar's own entries and leave a still-open sidebar
 * beneath it (and its tab history) untouched.
 */
type Layer =
  | { kind: 'modal' }
  | { kind: 'sidebar'; sidebar: Sidebar<SidebarType> }
  | { kind: 'tab'; sidebar: Sidebar<SidebarType>; prevTab: string };

export function useHistoryIntercept() {
  const modalStore = useModalStore();
  const sidebarStore = useSidebarStore();

  // The entries we pushed, in push order. `layers.length` === number of intercept
  // entries currently sitting on top of the real history.
  const layers: Layer[] = [];

  // Counters used to recognise the popstate events triggered by our *own*
  // history.back() calls (silent pops) and the tab change a back-navigation
  // restore causes, so we don't treat them as fresh user actions.
  let pendingSilentPops = 0;
  let pendingSilentTabChanges = 0;

  // Every intercept entry carries a monotonically increasing `__interceptSeq` in
  // its history.state, and we remember the seq of the entry we're currently on.
  // Comparing the destination entry's seq against `currentSeq` tells us a
  // popstate's *direction*: a lower seq means the user went back (we close the
  // matching layer); a higher seq means they pressed the browser *forward* button
  // onto an entry they had already dismissed — which we refuse to honour.
  let seqCounter: number = history.state?.__interceptSeq ?? 0;
  let currentSeq = seqCounter;

  const pushLayer = (layer: Layer) => {
    layers.push(layer);
    const seq = ++seqCounter;
    // Vue Router (createWebHistory) keeps its entire navigation state — `current`,
    // `back`, `forward`, `position`, `scroll` — in `history.state`. A bare
    // `pushState({ ... })` would REPLACE that object, wiping those fields. The next
    // router navigation/popstate then reads `history.state.current` as `undefined`
    // and builds a URL like `<base>undefined/`. So we spread the existing state to
    // preserve the router's bookkeeping and only add our own `__interceptSeq`.
    // Omitting the URL argument keeps the current URL, so this invisible intercept
    // entry doesn't change the address.
    history.pushState({ ...history.state, __interceptSeq: seq }, '');
    currentSeq = seq;
  };

  const popLayerSilently = () => {
    if (layers.length > 0) {
      layers.pop();
      pendingSilentPops++;
      history.back();
    }
  };

  // --- Push / pop history entries as layers open and close ---

  watch(
    () => modalStore.modalStack.filter((m) => !m.closing).length,
    (newLen, oldLen) => {
      if (oldLen === undefined) return;
      if (newLen > oldLen) {
        pushLayer({ kind: 'modal' });
      } else if (newLen < oldLen) {
        // Modal(s) closed programmatically (X button, confirm, etc.)
        popLayerSilently();
      }
    },
  );

  // Track the set of currently-open sidebars by identity so we can detect exactly
  // which sidebar opened or closed (rather than just that the count changed).
  // Inline cascade columns (PRD #963) are mounted from the URL, so their history
  // entries are owned by the router — skip them here to avoid double-push.
  const isOverlay = (s: Sidebar<SidebarType>) => !s.closing && !s.inline;
  let openSidebars: Sidebar<SidebarType>[] = sidebarStore.sidebarStack.filter(isOverlay);

  watch(
    () => sidebarStore.sidebarStack.filter(isOverlay),
    (open) => {
      const added = open.filter((s) => !openSidebars.includes(s));
      const removed = openSidebars.filter((s) => !open.includes(s));
      openSidebars = open;

      for (let i = 0; i < added.length; i++) {
        pushLayer({ kind: 'sidebar', sidebar: added[i] });
      }

      if (removed.length === 0) return;

      // Silently unwind every entry at the top of the stack that belongs to a
      // sidebar that just closed (its tab entries first, then its own entry).
      // We stop as soon as the top entry belongs to a sidebar that is still open,
      // so its tab history survives. (If a non-top sidebar is closed
      // programmatically — not a normal flow, since the close/back-drop buttons
      // act on the active top sidebar — its buried entries can't be popped from a
      // linear history and are simply left in place.)
      const removedSet = new Set<Sidebar<SidebarType>>(removed);
      while (layers.length > 0) {
        const top = layers[layers.length - 1];
        if (top.kind !== 'modal' && removedSet.has(top.sidebar)) {
          popLayerSilently();
        } else {
          break;
        }
      }
    },
  );

  // Track sidebar tab changes. A tab switch on the active (top) sidebar pushes a
  // layer recording the *previous* tab, so back navigation can restore it.
  watch(
    () => sidebarStore.sidebarTabView,
    (newTab, oldTab) => {
      if (!oldTab || !newTab || oldTab === newTab) return;
      if (pendingSilentTabChanges > 0) {
        // This change was caused by our own back navigation (restoring a previous
        // tab); the popped history entry was already consumed, so don't push.
        pendingSilentTabChanges--;
        return;
      }
      const topSidebar = [...sidebarStore.sidebarStack].reverse().find((s) => !s.closing);
      // Tab changes inside an inline cascade column are owned by the URL
      // (`cascadeStore.setColumnTab` pushes them as real entries), so the browser
      // already walks them back on its own; don't intercept here.
      if (topSidebar && !topSidebar.inline && topSidebar.sidebarTabItems.length > 1) {
        pushLayer({ kind: 'tab', sidebar: topSidebar, prevTab: oldTab });
      }
    },
  );

  // --- Handle back button (popstate) ---

  const onPopState = (event: PopStateEvent) => {
    const destSeq: number = event.state?.__interceptSeq ?? 0;

    if (pendingSilentPops > 0) {
      // This popstate was triggered by one of our own popLayerSilently calls.
      pendingSilentPops--;
      currentSeq = destSeq;
      return;
    }

    // Forward navigation: the user pressed the browser *forward* button — or picked
    // an entry several steps ahead from the history dropdown — and landed on an
    // intercept entry (higher seq) they had already dismissed via back. We never
    // re-open a modal/sidebar/tab from a forward press, so cancel it by traversing
    // straight back to where they were. A multi-entry forward jump fires a single
    // popstate, so we must step back the full `destSeq - currentSeq` distance in one
    // `history.go` (a lone `history.back()` would only undo one step and leave
    // `currentSeq`/`layers` drifting out of sync with the real history position).
    // The traversal fires exactly one popstate for the destination, so a single
    // silent-pop increment consumes it. Forward navigation to real routes (which
    // carry no seq) is left intact.
    if (destSeq > currentSeq) {
      pendingSilentPops++;
      history.go(currentSeq - destSeq);
      return;
    }

    currentSeq = destSeq;

    // Only intercept entries we pushed.
    if (layers.length === 0) return;
    const layer = layers.pop()!;

    if (layer.kind === 'modal') {
      const openModals = modalStore.modalStack.filter((m) => !m.closing);
      if (openModals.length > 0) {
        const topModal = openModals[openModals.length - 1];
        modalStore.closeModal({ type: topModal.modalType, action: 'cancel' });
      }
      return;
    }

    if (layer.kind === 'tab') {
      pendingSilentTabChanges++;
      sidebarStore.setSidebarView(layer.prevTab);
      return;
    }

    // layer.kind === 'sidebar' — close that sidebar (its own removal watcher will
    // see it was closed via back, with no remaining entries left to unwind).
    if (!layer.sidebar.closing) {
      sidebarStore.closeSidebar({ type: layer.sidebar.sidebarType, action: 'cancel' });
    }
  };

  window.addEventListener('popstate', onPopState);

  onUnmounted(() => {
    window.removeEventListener('popstate', onPopState);
  });
}
